/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button } from 'antd';

import { InfoCircleOutlined, ReloadOutlined } from '@ant-design/icons';

import Utils from '../../../../Assets/Scripts/Utils';
import useSignalR from '../../../../Hooks/useSignalR';
import { api } from '../../../../Services/axiosService';

import LocationsList from './LocationsList';
import MapComponent from './MapComponent';
import MapFullScreen from './MapFullScreen';
import OperatorSelector from './OperatorSelector';

import './MapPanel.scss';

// Painel principal de rastreamento
function MapPanel({ task, settings }) {
  const [loading, setLoading] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [trackingData, setTrackingData] = useState([]);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const hubConnection = useSignalR('taskTracking');
  const pendingUpdates = useRef([]);
  const updateTimeout = useRef(null);
  const BATCH_UPDATE_INTERVAL = 500; // ms

  // Monta a lista de operadores disponíveis (instrutores e motoristas)
  const availableOperators = useMemo(() => {
    const ops = [];
    const addOperator = (op, type, trackingType, extra = {}) => {
      if (op?.userId) {
        ops.push({
          id: String(op.userId),
          name: op.userName,
          type,
          trackingType,
          ...op,
          ...extra,
        });
      }
    };

    addOperator(task.mainInstructor, 'MainInstructor', 1);
    addOperator(task.assistantInstructor, 'AssistantInstructor', 2);
    task.driverList?.forEach((driver, idx) => addOperator(driver, 'Driver', 3, { index: idx }));

    return ops;
  }, [task.mainInstructor, task.assistantInstructor, task.driverList]);

  useEffect(() => {
    setSelectedOperator((currentSelected) => {
      if (currentSelected && availableOperators.some((op) => op.id === currentSelected.id)) {
        return availableOperators.find((op) => op.id === currentSelected.id);
      }
      return availableOperators.length > 0 ? availableOperators[0] : null;
    });
  }, [availableOperators]);

  // Busca os dados de rastreamento quando um operador é selecionado
  const fetchTrackingData = useCallback(() => {
    setLoading(true);
    setSelectedLocation(null);

    if (!selectedOperator || !task?.id) {
      setTrackingData([]);
      return;
    }

    api
      .get(
        `/TaskTracking/GetPointsByTaskAndOperator/${task.id}/${selectedOperator.id}/${selectedOperator.trackingType}`
      )
      .then((res) => setTrackingData(res.data || []))
      .catch(Utils.logError)
      .finally(() => setLoading(false));
  }, [selectedOperator, task]);

  useEffect(() => {
    fetchTrackingData();
  }, [fetchTrackingData]);

  // Junta-se ao grupo da tarefa no hub SignalR para receber atualizações em tempo real
  useEffect(() => {
    if (hubConnection) {
      hubConnection.on('ReceiveTaskTracking', (newPointList) => {
        const relevantPoints = newPointList.filter(
          (p) =>
            selectedOperator &&
            p.operatorId === parseInt(selectedOperator.id, 10) &&
            p.taskId === task.id
        );

        if (relevantPoints.length > 0) {
          pendingUpdates.current.push(...relevantPoints);
          if (!updateTimeout.current) {
            updateTimeout.current = setTimeout(() => {
              setTrackingData((prevData) => {
                const existingTimestamps = new Set(prevData.map((p) => p.timestamp));
                const uniqueNewPoints = pendingUpdates.current.filter(
                  (p) => !existingTimestamps.has(p.timestamp)
                );
                const newData = [...prevData, ...uniqueNewPoints];
                return newData.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
              });
              pendingUpdates.current = [];
              updateTimeout.current = null;
            }, BATCH_UPDATE_INTERVAL);
          }
        }
      });
    }

    return () => {
      if (hubConnection) {
        hubConnection.off('ReceiveTaskTracking');
      }
      if (updateTimeout.current) {
        clearTimeout(updateTimeout.current);
      }
    };
  }, [hubConnection, task?.id, selectedOperator]);

  if (availableOperators.length === 0) {
    return (
      <Alert
        message="Não há operadores vinculados a este treinamento."
        type="warning"
        showIcon
        icon={<InfoCircleOutlined />}
        className="custom-alert"
      />
    );
  }

  return (
    <div className="map-panel">
      <div className="map-panel-header">
        <OperatorSelector
          operators={availableOperators}
          selectedOperator={selectedOperator}
          onOperatorChange={setSelectedOperator}
          loading={loading}
        />
        <Button icon={<ReloadOutlined />} onClick={fetchTrackingData} disabled={loading} />
      </div>
      <div className="map-panel-content">
        <div className="map-wrapper">
          {(trackingData?.length > 0 || task.latitude) && (
            <MapComponent
              task={task}
              selectedOperator={selectedOperator}
              trackingData={trackingData}
              loading={loading}
              onFullScreenToggle={() => setIsFullScreenOpen(true)}
              selectedLocation={selectedLocation}
              onLocationSelect={setSelectedLocation}
              settings={settings}
            />
          )}
        </div>
        <div className="list-wrapper">
          <LocationsList
            trackingData={trackingData}
            loading={loading}
            selectedOperator={selectedOperator}
            onLocationSelect={setSelectedLocation}
          />
        </div>
      </div>
      {isFullScreenOpen && (
        <MapFullScreen
          isOpen={isFullScreenOpen}
          onClose={() => setIsFullScreenOpen(false)}
          task={task}
          trackingData={trackingData}
          selectedOperator={selectedOperator}
          selectedLocation={selectedLocation}
          onLocationSelect={setSelectedLocation}
          availableOperators={availableOperators}
          onOperatorChange={setSelectedOperator}
          loading={loading}
          settings={settings}
        />
      )}
    </div>
  );
}

export default MapPanel;
